import { useState, useCallback, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RestrictedActiveDealsHeader } from "@/components/properties/active/RestrictedActiveDealsHeader";
import { ActiveDealsSearch } from "@/components/properties/active/ActiveDealsSearch";
import { ActiveDealsTabContent } from "@/components/properties/active/ActiveDealsTabContent";
import { PageSizeSelector } from "@/components/properties/active/PageSizeSelector";
import { useActiveDeals, PropertyStatus } from "@/hooks/useActiveDeals";
import { usePropertyStatusCounts } from "@/hooks/usePropertyStatusCounts";

const STORAGE_KEY = 'activeDeals_pageSize';
const DEFAULT_PAGE_SIZE = 20;

const ActiveDealsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<PropertyStatus>("active");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored) : DEFAULT_PAGE_SIZE;
  });

  // Fetch counts for tab labels
  const { data: counts = { all: 0, active: 0, pending: 0, sold: 0, expired: 0, draft: 0 } } = usePropertyStatusCounts(searchTerm);

  // Fetch paginated properties
  const { properties, totalCount, isLoading } = useActiveDeals({
    page: currentPage,
    pageSize,
    status: activeTab,
    searchTerm
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  const daysOnSite = useCallback((createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  // Reset to page 0 when tab, search, or page size changes
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab, searchTerm, pageSize]);

  // Persist page size preference
  const handlePageSizeChange = (newSize: number) => {
    localStorage.setItem(STORAGE_KEY, newSize.toString());
    setPageSize(newSize);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as PropertyStatus);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      <RestrictedActiveDealsHeader />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <ActiveDealsSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList data-tour="deals-stats" className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="sold">Sold ({counts.sold})</TabsTrigger>
          <TabsTrigger value="expired">Expired ({counts.expired})</TabsTrigger>
          <TabsTrigger value="draft">Drafts ({counts.draft})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="mt-0" data-tour="property-grid">
          <ActiveDealsTabContent 
            properties={properties}
            isLoading={isLoading}
            searchTerm={searchTerm}
            daysOnSite={daysOnSite}
            tabContext="active"
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </TabsContent>
        
        <TabsContent value="pending" className="mt-0">
          <ActiveDealsTabContent 
            properties={properties}
            isLoading={isLoading}
            searchTerm={searchTerm}
            daysOnSite={daysOnSite}
            tabContext="pending"
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        <TabsContent value="sold" className="mt-0">
          <ActiveDealsTabContent 
            properties={properties}
            isLoading={isLoading}
            searchTerm={searchTerm}
            daysOnSite={daysOnSite}
            tabContext="sold"
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        <TabsContent value="expired" className="mt-0">
          <ActiveDealsTabContent 
            properties={properties}
            isLoading={isLoading}
            searchTerm={searchTerm}
            daysOnSite={daysOnSite}
            tabContext="expired"
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </TabsContent>

        <TabsContent value="draft" className="mt-0">
          <ActiveDealsTabContent 
            properties={properties}
            isLoading={isLoading}
            searchTerm={searchTerm}
            daysOnSite={daysOnSite}
            tabContext="draft"
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActiveDealsPage;
